import { AvatarSize } from "@components/Avatar";
import { NonprofitAvatar } from "@components/Avatar/NonprofitAvatar";
import { UserAvatar } from "@components/Avatar/UserAvatar";
import { Button, ButtonRole, ButtonTargetKind } from "@components/Button";
import { Checkbox } from "@components/Checkbox";
import { IconSize, IconDisplay, Icon } from "@components/Icon";
import {
  AutocompleteResult,
  AutocompleteResultsMap,
  AutocompleteSection,
  MAX_SEARCH_TERM_LENGTH,
  queryConstructor,
  QueryConstructorOptions,
  queryTypesense,
  SEARCH_DEBOUNCE_MS,
  SearchbarProps,
} from "@components/SearchTextInput";
import { TextInput, TextInputType } from "@components/TextInput";
import { css } from "@emotion/react";
import type { CSSInterpolation } from "@emotion/serialize";
import styled from "@emotion/styled";
import React, { forwardRef, useEffect, useState, useMemo } from "react";
import { useDebounce } from "use-debounce";

import { Username } from "@every.org/common/src/codecs/username";
import { SHARED_PALETTE } from "@every.org/common/src/display/palette";
import { TextSize } from "@every.org/common/src/display/text";
import { useTypesenseSearchSpec } from "@every.org/common/src/entity/types/configVariable";

import {
  useConfigVariable,
  UseConfigVariableStatus,
} from "src/context/ConfigVariableContext";
import { textButtonFocusCss } from "src/styles/button";
import { MAX_TEXT_INPUT_WIDTH_VAR } from "src/theme/GlobalStyle/inputStyles";
import { colorCssVars, lightBgThemeCss } from "src/theme/color";
import { BORDER_RADIUS } from "src/theme/common";
import { horizontalStackCss, spacing } from "src/theme/spacing";
import { secondaryMetadataTextCss, textSizeCss } from "src/theme/text";
import { useUserExperiment } from "src/utility/abtesting";
import { logger } from "src/utility/logger";

export interface SelectableAutocompleteResult extends AutocompleteResult {
  selected?: boolean;
}

const DROPDOWN_LINK_CSS = [
  lightBgThemeCss,
  horizontalStackCss.xs,
  textSizeCss[TextSize.xs],
  css`
    cursor: pointer;
    align-items: center;
    position: relative;
    padding: calc(${spacing.s} - 2px) calc(${spacing.m} - 2px);
    border: 2px transparent solid;
    &:focus-within {
      border: 2px ${SHARED_PALETTE.focus} solid;
    }

    &:hover {
      background: var(${colorCssVars.background.faded});
    }
  `,
];

const OnClickOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;
const EscapeOverlay = styled.div`
  position: fixed;
  top: -100vh;
  left: -100vw;
  width: calc(3 * 100vw);
  height: calc(3 * 100vh);
`;

interface SearchSelectorInputProps extends SearchbarProps {
  selector: AutocompleteSection;
  selectedSuggestions?: SelectableAutocompleteResult[];
  onSelectedChange?: (ids?: string[]) => void;
  setSelectedSuggestions?: (items: SelectableAutocompleteResult[]) => void;
  multiSelect?: boolean;
  placeholder?: string;
  selectionLimit?: number;
  dropDownCss?: CSSInterpolation;
  queryOptions?: QueryConstructorOptions;
}

const SearchSelectorWithRef = forwardRef<
  HTMLInputElement,
  SearchSelectorInputProps
>(function SearchSelectorInputComponent(
  {
    className,
    selector,
    selectedSuggestions: externalSelectedSuggestions,
    onSelectedChange,
    setSelectedSuggestions: externalSetSelectedSuggestions,
    multiSelect = true,
    selectionLimit,
    dropDownCss,
    queryOptions,
    ...rest
  },
  ref
) {
  const [inputValue, setInputValue] = useState<string>("");
  const [autocompleteResults, setAutocompleteResults] =
    useState<AutocompleteResultsMap>(new Map());
  const [searchError, setSearchError] = useState<boolean>(false);
  const [internalSelectedSuggestions, setInternalSelectedSuggestions] =
    useState<SelectableAutocompleteResult[]>(externalSelectedSuggestions ?? []);

  const selectedSuggestions =
    externalSelectedSuggestions ?? internalSelectedSuggestions;
  const setSelectedSuggestions =
    externalSetSelectedSuggestions ?? setInternalSelectedSuggestions;

  // <typesense experiment>
  const typesenseFlag = useConfigVariable(useTypesenseSearchSpec);
  const typesenseConfigEnabled =
    typesenseFlag.status === UseConfigVariableStatus.SUCCESS &&
    typesenseFlag.value === true;
  // Statsig experiment for Typesense search
  const typesenseExperiment = useUserExperiment("typesense_search", false);
  const experimentGroup = typesenseExperiment.get<string>("kind");
  // Determine search provider:
  // - Test group: Typesense only (no fallback)
  // - Control group: Constructor only
  // - Not in experiment: Use config variable
  const isInExperiment =
    experimentGroup === "constructor" || experimentGroup === "typesense";
  const useTypesense = isInExperiment
    ? experimentGroup === "typesense"
    : typesenseConfigEnabled;
  const noFallback = experimentGroup === "typesense";
  // </typesense experiment>

  const suggestions: SelectableAutocompleteResult[] = [
    ...selectedSuggestions,
    ...(autocompleteResults.get(selector) || []),
  ].filter((value, index, arr) => {
    return arr.findIndex(({ id }) => value.id === id) === index;
  });

  // Truncate before searching so a long paste still returns results instead
  // of a 414, and so typing past the limit doesn't keep re-triggering
  // searches - the effect below depends on this already-truncated value.
  // Also checks if a 16bits special character was split in two and removes
  // it if was
  let searchTerm = inputValue.slice(0, MAX_SEARCH_TERM_LENGTH);
  if (/[\uD800-\uDBFF]$/.test(searchTerm)) {
    searchTerm = searchTerm.slice(0, -1);
  }
  const [debouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    // Ignore results from a superseded request rather than racing on
    // timestamps, so an older query result never over-writes a newer
    // one when requests return out of order.
    // https://react.dev/learn/synchronizing-with-effects#fetching-data
    let ignore = false;
    async function autocomplete() {
      try {
        const searchResults = useTypesense
          ? await queryTypesense(debouncedSearchTerm, {
              ...queryOptions,
              noFallback,
            })
          : await queryConstructor(debouncedSearchTerm, queryOptions);
        if (ignore) {
          return;
        }
        setAutocompleteResults(searchResults);
        setSearchError(false);
      } catch (error) {
        // queryTypesense rethrows (rather than falling back to
        // Constructor.io) when noFallback is set, which would otherwise be
        // an unhandled rejection here.
        logger.error({
          message: "SearchSelectorInput autocomplete failed",
          error,
        });
        if (ignore) {
          return;
        }
        setAutocompleteResults(new Map());
        setSearchError(true);
      }
    }
    autocomplete();
    return () => {
      ignore = true;
    };
  }, [debouncedSearchTerm, queryOptions, useTypesense, noFallback]);
  // TODO: at some point we might want to just pass the whole object and let the user decide which attribute to use
  useEffect(() => {
    if (onSelectedChange) {
      onSelectedChange(
        selectedSuggestions.map(
          ({ id, slug, username }) => slug || username || id
        )
      );
    }
  });
  const [searchFocused, setSearchFocused] = useState(false);
  const showDropdown = searchFocused && (!!suggestions.length || searchError);

  const valueToDisplay = useMemo(() => {
    const category =
      selector === AutocompleteSection.NONPROFITS ? "nonprofit" : "user";
    const { length } = selectedSuggestions;

    // N users/nonprofits selected
    const placeholderText = multiSelect
      ? `${length} ${category}${length > 1 ? "s" : ""} selected`
      : `${selectedSuggestions[0]?.title}`;

    return !searchFocused && length > 0 ? placeholderText : inputValue;
  }, [selector, selectedSuggestions, multiSelect, searchFocused, inputValue]);

  return (
    <div
      className={className}
      css={css`
        position: relative;
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          > * {
            flex-grow: 1;
            &:not(:last-child) {
              margin-right: ${spacing.xs};
            }
          }
        `}
      >
        <TextInput
          inputBoxCss={css`
            z-index: 1;
          `}
          {...rest}
          onChange={(e) => {
            setInputValue(e.target.value);
          }}
          type={TextInputType.SEARCH}
          value={valueToDisplay}
          ref={ref}
          placeholder={rest.placeholder ?? `Search ${selector}`}
          inputPrefix={
            multiSelect || selectedSuggestions.length === 0 ? (
              <Icon
                iconImport={() => import("@components/Icon/icons/SearchIcon")}
                size={IconSize.MEDIUM}
                display={IconDisplay.SECONDARY}
                css={css`
                  margin-right: ${spacing.xs};
                `}
              />
            ) : (
              <NonprofitAvatar
                size={AvatarSize.XX_SMALL}
                disableLink
                nonprofit={{
                  logoCloudinaryId:
                    selectedSuggestions[0].cloudinary_id ||
                    selectedSuggestions[0].image_url ||
                    "",
                  primarySlug:
                    selectedSuggestions[0].slug ||
                    selectedSuggestions[0].id ||
                    "",
                  name:
                    selectedSuggestions[0].title ||
                    selectedSuggestions[0].slug ||
                    "",
                }}
                customCSS={css`
                  margin-right: ${spacing.xs};
                `}
              />
            )
          }
          setInputPrefixColorAndHeight={false}
          inputSuffix={
            (!!inputValue || !!selectedSuggestions.length) && (
              <Button
                data-tname="SearchTextInput--clear"
                css={textButtonFocusCss}
                role={ButtonRole.UNSTYLED}
                onClick={{
                  kind: ButtonTargetKind.FUNCTION,
                  action: (e) => {
                    e.stopPropagation();
                    if (inputValue.length > 0) {
                      setInputValue("");
                    } else {
                      setSearchFocused(false);
                      setSelectedSuggestions([]);
                    }
                  },
                }}
              >
                <Icon
                  iconImport={() => import("@components/Icon/icons/XIcon")}
                  size={IconSize.MEDIUM}
                  display={IconDisplay.ACCENT}
                />
              </Button>
            )
          }
          onFocus={() => {
            setSearchFocused(true);
          }}
          collapseDescriptionSpace
          aria-haspopup="true"
          aria-expanded={showDropdown}
        />
      </div>
      {showDropdown && (
        <React.Fragment>
          {!rest.alwaysOpen && (
            <EscapeOverlay
              onClick={() => {
                setSearchFocused(false);
              }}
            />
          )}
          <ul
            data-tname="SearchSelectorDropdown"
            css={[
              css`
                position: absolute;
                left: 0;
                right: 0;
                top: 66px;
                z-index: 3;
                max-width: var(${MAX_TEXT_INPUT_WIDTH_VAR});
                border-radius: ${BORDER_RADIUS};
                border: 1px solid rgba(46, 52, 52, 0.1);
                background: var(${colorCssVars.background.normal});
                box-shadow: 0px 8px 32px -16px rgba(46, 52, 52, 0.2);
                padding: ${spacing.xs} 0;
              `,
              dropDownCss,
            ]}
          >
            {searchError ? (
              <li
                css={[
                  secondaryMetadataTextCss,
                  css`
                    padding: calc(${spacing.s} - 2px) calc(${spacing.m} - 2px);
                  `,
                ]}
              >
                Search is temporarily unavailable. Please try again later.
              </li>
            ) : (
              suggestions.map((item, index, array) => {
                const onClick = () => {
                  if (item.selected) {
                    setSelectedSuggestions(
                      selectedSuggestions.filter(({ id }) => {
                        return id !== item.id;
                      })
                    );
                  } else {
                    if (multiSelect) {
                      if (
                        !selectionLimit ||
                        selectedSuggestions.length < selectionLimit
                      ) {
                        setSelectedSuggestions([
                          ...selectedSuggestions,
                          { ...item, selected: true },
                        ]);
                      }
                    } else {
                      setSelectedSuggestions([{ ...item, selected: true }]);
                      setSearchFocused(false);
                    }
                  }
                };
                return (
                  <li
                    css={DROPDOWN_LINK_CSS}
                    key={item.id}
                    data-tname={`SearchSelectorDropdown_Item-${index}`}
                  >
                    <OnClickOverlay onClick={onClick} />
                    {item.section === AutocompleteSection.USERS ? (
                      <UserAvatar
                        size={AvatarSize.XX_SMALL}
                        disableLink
                        user={{
                          profileImageCloudinaryId:
                            item.cloudinary_id || item.image_url || "",
                          username:
                            (item.username as Username) ||
                            (item.id as Username) ||
                            undefined,
                        }}
                      />
                    ) : (
                      <NonprofitAvatar
                        size={AvatarSize.XX_SMALL}
                        disableLink
                        nonprofit={{
                          logoCloudinaryId:
                            item.cloudinary_id || item.image_url || "",
                          primarySlug: item.slug || item.id || "",
                          name: item.title || item.slug || "",
                        }}
                      />
                    )}
                    <span
                      data-tname={`SearchSelectorDropdown_Item-${index}_Title`}
                    >
                      {item.title}
                    </span>
                    {multiSelect && (
                      <Checkbox
                        css={css`
                          margin-left: auto;
                        `}
                        data-tname={`${selector}SearchItem`}
                        checked={item.selected}
                      />
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </React.Fragment>
      )}
    </div>
  );
});
export const SearchSelectorInput = React.memo(SearchSelectorWithRef);
